import { useState } from 'react';
import Trans from 'next-translate/Trans';
import Cookies from 'js-cookie';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';

//Components
import InfoBanner from '@/components/base/InfoBanner';
import LinkBase from '@/components/base/LinkBase';

//routes
import { DATA_ACCESS, SOCIAL_PROFILES, SUBSCRIPTION_INVOICES } from '@/routes/privateRoutes';

// Libs
import { CHANGE_PAYMENT_METHOD } from '@/lib/settings/subscriptionInvoices/steps';

//Hooks
import useTranslation from 'next-translate/useTranslation';
import useSocialProfiles from '@/hooks/swr/useSocialProfiles';
import useUserFBPermissions from '@/hooks/swr/useUserFBPermissions';
import useSubscription from '@/utils/hooks/swr/pricing/useSubscription';
import useUserRole from '@/hooks/user/useUserRole';
import useUser from '@/hooks/swr/useUser';
import useActiveSocialProfile from '@/utils/hooks/user/useActiveSocialProfile';

dayjs.extend(isBetween);

const SIGN_UP_DONE = 'sign_up_done';

const GlobalBannerComponent = ({
    cookie = '',
    title = '',
    text = '',
    type = 'error',
    condition = null,
    noClose = false
}) => {
    const [open, setOpen] = useState(true);
    if (condition && open) {
        return (
            <InfoBanner
                type={type}
                title={title}
                message={text}
                onClose={
                    noClose
                        ? null
                        : () => {
                              setOpen(false);
                              Cookies.set(cookie, 'true', { expires: 1 });
                          }
                }
            />
        );
    }

    return null;
};

GlobalBannerComponent.propTypes = {
    cookie: PropTypes.string,
    title: PropTypes.string,
    text: PropTypes.object,
    type: PropTypes.string,
    condition: PropTypes.bool,
    noClose: PropTypes.bool
};

const validateCookie = (cookie) =>
    typeof Cookies.get(cookie) === 'undefined' || Cookies.get(cookie) !== 'true';

const BANNER_REAUTH_SP = 'banner-reauth-sp';
const BANNER_DATA_ACCESS_EXPIRED = 'banner-data-access-expired';
const BANNER_DATA_ACCESS_EXPIRED_5D = 'banner-data-access-expired-5D';
const BANNER_REMINDER_PLAN_CANCELLATION_7D = 'banner-reminder-plan-cancellation-7D';

const GlobalBanners = () => {
    const { t } = useTranslation();
    const router = useRouter();
    const { errorAuthDataAccessSocialProfiles, errorAuthOtherSocialProfiles } = useSocialProfiles();
    const { user: userFBPermissions, isLoading } = useUserFBPermissions();
    const { subscription: plan, isOnTrial } = useSubscription();
    const { isTeamMember, isAdmin } = useUserRole();
    const { user } = useUser({
        revalidateOnMount: false,
        revalidateOnFocus: false,
        revalidateOnReconnect: false
    });
    const { activeSocialProfile } = useActiveSocialProfile();

    const hasReAuthError =
        errorAuthDataAccessSocialProfiles?.length || errorAuthOtherSocialProfiles?.length;

    const banners = [
        {
            id: 'banner-trial',
            title: t('errors:trial_ended_title'),
            text: <Trans i18nKey={'errors:trial_ended'} />,
            type: 'error',
            condition: !user?.subscribed_or_trial,
            noClose: true
        },
        {
            id: 'banner-full-stats-uncompleted',
            title: t('commons:banners.wanning_full_stats_uncompleted_title'),
            text: <Trans i18nKey="commons:banners.wanning_full_stats_uncompleted_text" />,
            type: 'info',
            condition:
                activeSocialProfile?.first_stats &&
                !activeSocialProfile?.full_stats &&
                dayjs().utc().diff(dayjs(activeSocialProfile.created_at), 'days') < 2 &&
                validateCookie(`banner-full-stats-uncompleted-${activeSocialProfile?.id}`),
            cookie: `banner-full-stats-uncompleted-${activeSocialProfile?.id}`
        },
        {
            id: 'banner-past-due',
            title: t('commons:banners.error_billing_past_due_title'),
            text: (
                <Trans
                    i18nKey={
                        isAdmin
                            ? 'commons:banners.error_billing_past_due_text.admin'
                            : 'commons:banners.error_billing_past_due_text.not_admin'
                    }
                    components={{
                        Link: (
                            <LinkBase
                                href={{
                                    pathname: SUBSCRIPTION_INVOICES,
                                    query: { step: CHANGE_PAYMENT_METHOD }
                                }}
                                className="underline"
                            />
                        )
                    }}
                />
            ),
            type: 'error',
            condition: user?.past_due,
            noClose: true
        },
        {
            id: BANNER_REAUTH_SP,
            title: t(
                isTeamMember
                    ? 'commons:banners.error_team_members_social_profiles_auth_title'
                    : 'commons:banners.error_social_profiles_auth_title'
            ),
            text: (
                <Trans
                    i18nKey={
                        isTeamMember
                            ? 'commons:banners.error_team_members_social_profiles_auth_text'
                            : router?.asPath !== SOCIAL_PROFILES
                              ? 'commons:banners.error_social_profiles_others_auth_text'
                              : 'commons:banners.error_social_profiles_others_auth_text_social_profiles'
                    }
                    components={{
                        Link: <LinkBase href={SOCIAL_PROFILES} className="underline" />
                    }}
                />
            ),
            type: 'error',
            condition: hasReAuthError && validateCookie(BANNER_REAUTH_SP),
            cookie: BANNER_REAUTH_SP
        },
        {
            id: BANNER_DATA_ACCESS_EXPIRED,
            title: t('commons:banners.error_data_access_expired_title'),
            text: (
                <Trans
                    i18nKey={
                        router?.asPath !== DATA_ACCESS
                            ? 'commons:banners.error_data_access_expired_text'
                            : 'commons:banners.error_data_access_expired_text_data_access'
                    }
                    components={{
                        Link: <LinkBase href={DATA_ACCESS} className="underline" />
                    }}
                />
            ),
            type: 'error',
            condition:
                !isLoading &&
                !isTeamMember &&
                !userFBPermissions?.not_applicable &&
                (userFBPermissions?.refresh_date === null ||
                    dayjs(userFBPermissions?.refresh_date).diff(dayjs()) <= 0) &&
                validateCookie(BANNER_DATA_ACCESS_EXPIRED),
            cookie: BANNER_DATA_ACCESS_EXPIRED
        },
        {
            id: BANNER_DATA_ACCESS_EXPIRED_5D,
            title: t('commons:banners.error_data_access_expired_in_5D_title'),
            text: (
                <Trans
                    i18nKey={
                        router?.asPath !== DATA_ACCESS
                            ? 'commons:banners.error_data_access_expired_in_5D_text'
                            : 'commons:banners.error_data_access_expired_in_5D_text_data_access'
                    }
                    values={{
                        count: dayjs(userFBPermissions?.refresh_date).diff(dayjs(), 'day') + 1
                    }}
                    components={{
                        Link: <LinkBase href={DATA_ACCESS} className="underline" />
                    }}
                />
            ),
            type: 'error',
            condition:
                !isLoading &&
                !isTeamMember &&
                !userFBPermissions?.not_applicable &&
                userFBPermissions?.refresh_date !== null &&
                dayjs().isBetween(
                    dayjs(userFBPermissions?.refresh_date).subtract(5, 'day'),
                    dayjs(userFBPermissions?.refresh_date)
                ) &&
                validateCookie(BANNER_DATA_ACCESS_EXPIRED_5D),
            cookie: BANNER_DATA_ACCESS_EXPIRED_5D
        },
        {
            id: BANNER_REMINDER_PLAN_CANCELLATION_7D,
            title: t('commons:banners.reminder_plan_cancellation_7D_title'),
            text: (
                <Trans
                    i18nKey={
                        isTeamMember
                            ? 'commons:banners.reminder_team_members_plan_cancellation_7D_text'
                            : router?.asPath !== SUBSCRIPTION_INVOICES
                              ? 'commons:banners.reminder_plan_cancellation_7D_text'
                              : 'commons:banners.reminder_plan_cancellation_7D_text_subscription'
                    }
                    values={{ count: dayjs(plan?.next_renewal).diff(dayjs(), 'day') }}
                    components={{
                        Link: <LinkBase href={SUBSCRIPTION_INVOICES} className="underline" />
                    }}
                />
            ),
            type: 'warning',
            condition:
                !isOnTrial &&
                plan?.is_waiting_for_cancel &&
                plan?.next_renewal !== null &&
                dayjs().isBetween(
                    dayjs(plan?.next_renewal).subtract(8, 'day'),
                    dayjs(plan?.next_renewal)
                ) &&
                validateCookie(BANNER_REMINDER_PLAN_CANCELLATION_7D),
            cookie: BANNER_REMINDER_PLAN_CANCELLATION_7D
        }
    ];

    const bannersResult = banners?.filter((element) => element.condition === true);

    //Add condition on signup step to prevent banner from showing up in the signup funnel
    if (!bannersResult?.length || user?.sign_up_step !== SIGN_UP_DONE) return null;

    return (
        <div className="z-[3] flex flex-col space-y-4 md:mt-m">
            {bannersResult?.map((item) => {
                return (
                    <GlobalBannerComponent
                        key={item?.id}
                        title={item?.title}
                        text={item?.text}
                        type={item?.type}
                        condition={item?.condition}
                        cookie={item?.cookie}
                        noClose={item?.noClose}
                    />
                );
            })}
        </div>
    );
};

export default GlobalBanners;
